/**
 * =============================================================================
 * ManualCadenceStepDialog
 *
 * Dialog compacto para **edição** de um step manual de cadência a partir de
 * qualquer contexto (Listagem de Atividades, futuros widgets, etc.).
 *
 * Espelha o dialog "Editar atividade manual" já existente na aba Cadência do
 * modal de Negócio (`DealCadenceTab`), mas isolado num componente auto-contido
 * que não depende de estado local da aba. Reaproveita a mutation
 * `updateManualStep` de `useDealCadence` — cujo backend é a RPC
 * `update_manual_cadence_step` (com validação de tenant + permissões).
 *
 * Regras:
 *  - Só faz sentido para steps de cadência com `is_manual = true` e status
 *    pendente/em curso/atrasado. O gate visual é responsabilidade do caller.
 *  - Atualiza cache de listagem de atividades e da timeline do negócio após
 *    salvar (paridade com o comportamento da aba Cadência).
 *
 * @version 1.0.0
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Phone, Mail, Calendar as CalendarIcon, ListChecks, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useDealCadence } from '@/hooks/useDealCadence';

// -----------------------------------------------------------------------------
// Tipos e presets visuais (mantém paridade com DealCadenceTab)
// -----------------------------------------------------------------------------

/** Formato mínimo do step para pré-preenchimento do formulário. */
export interface ManualStepDraft {
  step_id: string;
  activity_type?: string | null;
  title?: string | null;
  due_at?: string | null;
  description_html?: string | null;
  phase_id?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deal id do step — necessário para o hook `useDealCadence` invalidar caches corretos. */
  dealId: string | null;
  /** Step manual a ser editado. */
  step: ManualStepDraft | null;
  /** Callback opcional disparado após um save bem-sucedido. */
  onSaved?: () => void;
}

// Ícones/hover/active idênticos ao dock do DealCadenceTab
const MANUAL_ACTIVITY_TYPES: Array<{ value: string; label: string; icon: typeof Phone }> = [
  { value: 'call', label: 'Ligação', icon: Phone },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'email', label: 'E-mail', icon: Mail },
  { value: 'meeting', label: 'Reunião', icon: CalendarIcon },
  { value: 'task', label: 'Tarefa', icon: ListChecks },
];

const HOVER: Record<string, string> = {
  call: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30 hover:text-emerald-700',
  whatsapp: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30 hover:text-emerald-700',
  email: 'hover:bg-violet-100 dark:hover:bg-violet-900/30 hover:text-violet-700',
  meeting: 'hover:bg-orange-100 dark:hover:bg-orange-900/30 hover:text-orange-700',
  task: 'hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-700',
};
const ACTIVE: Record<string, string> = {
  call: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  whatsapp: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  email: 'bg-violet-100 text-violet-700 border-violet-300',
  meeting: 'bg-orange-100 text-orange-700 border-orange-300',
  task: 'bg-amber-100 text-amber-700 border-amber-300',
};

// -----------------------------------------------------------------------------
// Componente
// -----------------------------------------------------------------------------

export function ManualCadenceStepDialog({ open, onOpenChange, dealId, step, onSaved }: Props) {
  // Hook do negócio dono do step — só faz sentido quando temos dealId.
  const { updateManualStep } = useDealCadence(dealId ?? null);

  // Estado local do formulário (paridade com o dialog inline do DealCadenceTab)
  const [activityType, setActivityType] = useState<string>('task');
  const [title, setTitle] = useState<string>('');
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [time, setTime] = useState<string>('09:00');
  const [description, setDescription] = useState<string>('');

  // Preenche os campos ao abrir com um novo step
  useEffect(() => {
    if (!open || !step) return;
    setActivityType(step.activity_type || 'task');
    setTitle(step.title || '');
    const due = step.due_at ? new Date(step.due_at) : new Date();
    setDate(due);
    const pad = (n: number) => String(n).padStart(2, '0');
    setTime(`${pad(due.getHours())}:${pad(due.getMinutes())}`);
    setDescription(step.description_html || '');
  }, [open, step]);

  const handleSubmit = async () => {
    if (!step?.step_id || !title.trim() || !date) return;
    const [h, m] = time.split(':').map((v) => parseInt(v, 10) || 0);
    const due = new Date(date);
    due.setHours(h, m, 0, 0);

    try {
      await updateManualStep.mutateAsync({
        stepId: step.step_id,
        activityType,
        title: title.trim(),
        dueAt: due.toISOString(),
        descriptionHtml: description.trim() ? description.trim() : null,
        phaseId: step.phase_id ?? null,
      });
      toast({ title: 'Atividade atualizada' });
      onOpenChange(false);
      onSaved?.();
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] p-4 max-h-[85dvh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-sm">Editar atividade manual</DialogTitle>
          <DialogDescription className="text-xs">
            Ajuste os dados desta atividade ad-hoc. Alterações não impactam métricas de aderência ao playbook.
          </DialogDescription>
        </DialogHeader>

        {/* Miolo rolável — evita que o modal cresça além da viewport */}
        <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-2 mt-1">
          <div className="space-y-2">
            <div>
              <Label className="text-xs">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Ligação de follow-up"
                className="h-8 text-xs"
                maxLength={200}
              />
            </div>

            {/* Tipo — dock horizontal com ícones */}
            <div>
              <Label className="text-xs">Tipo</Label>
              <TooltipProvider delayDuration={200}>
                <div className="flex items-center gap-1.5 rounded-lg border bg-muted/30 p-1.5">
                  {MANUAL_ACTIVITY_TYPES.map((a) => {
                    const isActive = activityType === a.value;
                    const hover = HOVER[a.value] ?? 'hover:bg-accent';
                    const active = ACTIVE[a.value] ?? '';
                    return (
                      <Tooltip key={a.value}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setActivityType(a.value)}
                            className={cn(
                              'h-9 w-9 rounded-md inline-flex items-center justify-center border border-transparent text-muted-foreground transition-colors',
                              isActive ? active : hover,
                            )}
                            aria-label={a.label}
                            aria-pressed={isActive}
                          >
                            <a.icon className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{a.label}</TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </TooltipProvider>
            </div>

            <div>
              <Label className="text-xs">Hora</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="h-8 text-xs w-32"
              />
            </div>
          </div>

          <Label className="text-xs mt-2 block">
            Data <span className="text-destructive">*</span>
          </Label>
          <Calendar
            mode="single"
            locale={ptBR}
            selected={date}
            onSelect={(d) => setDate(d ?? undefined)}
            initialFocus
            className={cn('p-0 pointer-events-auto scale-95 origin-top')}
          />

          <Label className="text-xs mt-2 block">Descrição (opcional)</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Detalhes ou instruções para esta atividade"
            rows={3}
            className="text-xs"
            maxLength={2000}
          />
        </div>

        <DialogFooter className="mt-3 gap-2 sm:gap-2 shrink-0">
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            disabled={!title.trim() || !date || updateManualStep.isPending}
            onClick={handleSubmit}
          >
            {updateManualStep.isPending ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
